/* eslint-disable max-lines */
// internal-deps
import {
    PaymentMethod,
    PaymentMethodMeta,
    PaymentGroupData,
    PaymentGroupMeta,
    IUpiPaymentGroup,
    ICardPaymentGroup,
    INetBankingPaymentGroup,
    IPreferredPaymentGroup,
    CardPaymentMethodMeta,
    NetBankingPaymentMethodMeta,
    UpiPaymentMethod,
    CardPaymentMethodData,
    NetBankingPaymentMethodData,
    PaymentGroupDataDef,
    PaymentMethodBalanceInfo,
    generatePaymentMethodId,
    SplitPayData,
    TICK_STATES,
} from "@swiggy-private/payment-ui";
import {
    VALID_PAYMENT_GROUP_TYPES,
    VALID_PAYMENT_METHODS,
    isEmpty,
    MARKETPLACE_BUSINESS_LINE,
    VALID_WALLET_LINK_STATUSES,
    OrderData,
} from "@swiggy-private/payment-core";
import { Option, Some, None } from "@swiggy-private/js-fp";
import { format, captureError, getCloudinaryUrl, isiOSPlatform, isAndroidPlatform } from "@swiggy-private/core-ui";
import {
    getBridgeSdk,
    SwgyHeaderKeysList,
    GlobalHeaderState,
    CheckoutRoute,
    CheckoutActionTypes,
    PlaceOrderData,
    isMROFlow,
} from "@swiggy-private/checkout-core";

// constants
import * as TextDict from "@payment-client/constants/TextDict";
import {
    DEFAULT_TICK_STATE,
    PAY_ON_DELIVERY_TICK_STATE,
    POST_ORDER_TIPPING_TICK_STATE,
    SUPER_TICK_STATE,
    POST_ORDER_PRIORITY_DELIVERY_STATE,
    SWIGGY_MONEY_TICK_STATE,
    DINEOUT_TICK_STATE,
    ConfirmOrderTextsPayload,
    SWIGGY_MONEY_GENERIC_TICK_STATE,
    SWIGGY_MONEY_GENERIC_TICK_STATE_GIFT,
    INSTAMART_TICK_STATE,
    PAY_ON_DELIVERY_TOING_TICK_STATE,
    DEFAULT_TOING_TICK_STATE,
    PENNY_DROP_TICK_STATE,
    ASSURE_PAY_ALL_DUES_TICK_STATE,
    DINEOUT_STEAL_DEAL_TICK_STATE,
    JUST_TICK_STATE,
} from "@payment-client/constants/ConfirmScreen";
import { ASSURE_PAY_ALL_DUES_STORAGE_KEY, GIFT_DETAILS_STORAGE_KEY } from "@payment-client/constants/StorageKeys";
import { IMetaData, PAYMENT_CODES } from "@swiggy-private/payment-types";

// helpers
import {
    isBusinessLineSwiggyMoney,
    isBusinessLineSuper,
    isBusinessLineDash,
    isOriginalBusinessLineSwiggyMoney,
    isBusinessLineSwiggyOne,
    isBusinessLineDineout,
    isCategoryInstamart,
    isBusinessLineRegular,
} from "./Misc";
import { isSplitPayMethodRewardAndSelected } from "@swiggy-private/payment-ui";

// interfaces
import {
    GetPaymentMethodsResponse,
    FRICTION_LAYOUT_ID,
    PresentationDetails,
} from "@payment-client/interfaces/PaymentApi";
import { ProcessOrderActionPayload } from "@payment-client/interfaces/Checkout";
import { GetWalletBalanceResponse } from "@payment-client/interfaces/WalletApi";

//internal-deps
import { CheckoutMetaInfoResponsePayload } from "@swiggy-private/native-bridge";
import { Tenant } from "@swiggy-private/native-bridge/dist/interfaces/Tenant";
import { safeJsonParse } from "@swiggy-private/js-utils";
import { PreebookPrepaid } from "@payment-client/constants/Order";

const isSessionStorageAvailable = typeof window !== "undefined" && typeof sessionStorage !== "undefined";

export { generatePaymentMethodId };

export function isPreferredGroup(
    group: PaymentGroupData<PaymentGroupMeta, PaymentMethod<PaymentMethodMeta>>,
): group is IPreferredPaymentGroup["paymentData"] {
    return group.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.PREFERRED;
}

export function getPreferredMethodGroupName(
    group: PaymentGroupData<PaymentGroupMeta, PaymentMethod<PaymentMethodMeta>>,
): string | undefined {
    return Array.isArray(group.paymentMethods) ? group.paymentMethods[0]?.groupName : undefined;
}

export function isUPIGroup(
    group: PaymentGroupData<PaymentGroupMeta, PaymentMethod<PaymentMethodMeta>>,
): group is IUpiPaymentGroup["paymentData"] {
    return group.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.UPI;
}

export function isNetBankingGroup(
    group: PaymentGroupData<PaymentGroupMeta, PaymentMethod<PaymentMethodMeta>>,
): group is INetBankingPaymentGroup["paymentData"] {
    return group.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.NETBANKING;
}

export function isCardGroup(
    group: PaymentGroupData<PaymentGroupMeta, PaymentMethod<PaymentMethodMeta>>,
): group is ICardPaymentGroup["paymentData"] {
    return (
        group.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.CARD ||
        group.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.FOOD_CARD
    );
}

export function isRewardsGroup(
    group: PaymentGroupData<PaymentGroupMeta, PaymentMethod<PaymentMethodMeta>>,
): group is ICardPaymentGroup["paymentData"] {
    return group.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.REWARDS;
}

export function getPaymentGroup(
    paymentResponse: GetPaymentMethodsResponse,
    groupType: string,
): PaymentGroupDataDef | undefined {
    return paymentResponse.paymentGroup.find((data) => data.groupName.toUpperCase() === groupType);
}

export const getExperimentValue = (
    paymentResponse: GetPaymentMethodsResponse | null | undefined,
    key: string,
): unknown | undefined => {
    return paymentResponse?.XPData?.[key];
};

export const getNewExperimentValue = (
    paymentResponse: GetPaymentMethodsResponse | null | undefined,
    key: string,
): unknown | undefined => {
    return paymentResponse?.xpData?.[key];
};

export const isAddNewCardPayment = (data: PaymentMethod<PaymentMethodMeta>): boolean => {
    return (
        data.name.toUpperCase() === VALID_PAYMENT_METHODS.NEW_CARD ||
        data.name.toUpperCase() === VALID_PAYMENT_METHODS.NEW_FOOD_CARD
    );
};

export const isInAppUpiOnboardingPayment = (data: PaymentMethod<PaymentMethodMeta>): boolean => {
    return data.name.toUpperCase() === VALID_PAYMENT_METHODS.NEW_INAPP_UPI_ONBOARDING;
};

export const isNewInAppUpiPayment = (data: PaymentMethod<PaymentMethodMeta>): boolean => {
    return data.name.toUpperCase() === VALID_PAYMENT_METHODS.NEW_INAPP_UPI;
};

export const isSwiggyMoneySelected = (
    selectedPaymentId: Option<string>,
    data: PaymentMethod<PaymentMethodMeta>,
): boolean => {
    return (
        data.name.toUpperCase() === VALID_PAYMENT_METHODS.SWIGGYPAY &&
        selectedPaymentId.unwrapOr("") === generatePaymentMethodId(data)
    );
};

export function getPaymentMethodFromGroup(
    group: PaymentGroupData<PaymentGroupMeta, PaymentMethod<PaymentMethodMeta>>,
    methodName: string,
): Option<PaymentMethod<PaymentMethodMeta>> {
    return Some(
        group.paymentMethods.find((paymentMethod) => paymentMethod.name.toLowerCase() === methodName.toLowerCase()),
    );
}

export function isPaymentMethodEnabled(method: PaymentMethod<PaymentMethodMeta>): boolean {
    return method.enabled;
}

export const getQuantityString = (quantity?: number): string => {
    if (quantity) {
        return quantity === 1 ? `${quantity} ${TextDict.ITEM}` : `${quantity} ${TextDict.ITEMS}`;
    }

    return `-- ${TextDict.ITEMS}`;
};

export const getPaymentHeaderSubtitle = (
    amount?: string | number,
    discountMessage?: string,
    couponCode?: string,
): string => {
    if (isBusinessLineSwiggyMoney() && amount != null) return format(TextDict.ADD_SWIGGYMONEY_SUBTITLE, String(amount));

    if (couponCode && discountMessage) {
        return `${couponCode} | ${discountMessage}`;
    } else if (couponCode) {
        return couponCode;
    } else if (discountMessage) {
        return discountMessage;
    }
    return "";
};

export const getPaymentHeaderSubtitleV4 = ({
    itemCount,
    amount,
    paymentResponse,
}: {
    itemCount: number | undefined;
    amount: string;
    paymentResponse?: GetPaymentMethodsResponse | null;
}): string => {
    // for MRO and AIP the payment header is driven by BE instead of App
    if (isBusinessLineDash() || (isMROFlow() && isBusinessLineRegular())) {
        if (isEmpty(paymentResponse)) {
            return "";
        }
        return paymentResponse?.presentationDetails?.headerSubtitle
            ? `${paymentResponse.presentationDetails.headerSubtitle} \u20B9${amount}`
            : format(TextDict.TO_PAY_X, amount);
    }

    if (isBusinessLineSuper()) {
        if (amount === "0") {
            return "";
        }
        return format(TextDict.SWIGGY_ONE_SUBTITLE, amount);
    } else if (!itemCount || isNaN(itemCount)) {
        if (amount === "0") {
            return "";
        }
        // TODO - cleanup required after feature is over
        if (
            isBusinessLineSwiggyMoney() &&
            amount != null &&
            isSessionStorageAvailable &&
            sessionStorage.getItem(GIFT_DETAILS_STORAGE_KEY)
        )
            return format(TextDict.ADD_SWIGGYMONEY_GIFTING_SUBTITLE, String(amount));

        if (isBusinessLineSwiggyMoney() && amount != null)
            return format(TextDict.ADD_SWIGGYMONEY_SUBTITLE, String(amount));

        return format(TextDict.TO_PAY_X, amount);
    }

    return format(itemCount > 1 ? TextDict.X_ITEMS_TOTAL_Y : TextDict.X_ITEM_TOTAL_Y, String(itemCount), amount);
};

export const getPaymentHeaderDiscountMessage = ({
    checkoutMetaInfo,
    presentationDetails,
}: {
    checkoutMetaInfo?: CheckoutMetaInfoResponsePayload | null;
    presentationDetails?: PresentationDetails;
}): string => {
    // for MRO and AIP the payment header is driven by BE instead of App
    if (isBusinessLineDash() || (isMROFlow() && isBusinessLineRegular())) {
        return presentationDetails?.discountMessage || "";
    }

    if (checkoutMetaInfo?.discountMessage) {
        return format(TextDict.X_OFFER, checkoutMetaInfo.discountMessage);
    }
    return "";
};

export const getPaymentHeaderTitle = (totalAmount?: string | number): string => {
    const businessLine = GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceBusinessLine) ?? "";
    const amount = totalAmount != null ? `₹${totalAmount}` : "--";
    const paymentAmountPrefix =
        TextDict.PAYMENT_HEADER_TITLE[businessLine.toLowerCase()] || TextDict.PAYMENT_HEADER_TITLE.default;

    return format(paymentAmountPrefix, String(amount));
};

export const getPaymentHeaderTitleV4 = (presentationDetails?: PresentationDetails): string => {
    if (presentationDetails?.headerTitle) {
        return presentationDetails.headerTitle;
    }

    const businessLine = GlobalHeaderState.getHeader(SwgyHeaderKeysList.marketplaceBusinessLine) ?? "";
    const title =
        TextDict.PAYMENT_HEADER_TITLE_V4[businessLine.toLowerCase()] || TextDict.PAYMENT_HEADER_TITLE_V4.default;
    return title;
};

export const isPhonepeSdkAvailable = async (tenant?: string): Promise<boolean> => {
    const platform = (GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "").toLowerCase();
    const isAndroid = isAndroidPlatform(platform);

    // isPhonepeSdkAvailable is not available on iOS
    if (isAndroid) {
        const data = await getBridgeSdk().isPhonepeSdkAvailable({
            ...(!isEmpty(tenant) && { tenant: tenant as Tenant }),
        });
        if (!isEmpty(data) && data?.isAvailable) {
            return true;
        }
    }

    return false;
};

export const upiIntentAvailableMethods = async (methods: string[]): Promise<string[]> => {
    if (isEmpty(methods)) {
        return [];
    }

    const data = await getBridgeSdk().upiIntentAvailableMethods({ methods });
    if (!data || !data.methods) {
        return [];
    }
    return data.methods;
};

type PaymentGroups = GetPaymentMethodsResponse["paymentGroup"];

export const getCouponApplicableMethods = (
    paymentMethods: PaymentMethod<PaymentMethodMeta>[],
): PaymentMethod<PaymentMethodMeta>[] => {
    return paymentMethods.filter((paymentMethod) => paymentMethod.couponApplicable);
};

export const getRewardApplicableMethods = (
    paymentMethods: PaymentMethod<PaymentMethodMeta>[],
): PaymentMethod<PaymentMethodMeta>[] => {
    return paymentMethods.filter((paymentMethod) => paymentMethod.rewardApplicable);
};

const _splitPaymentGroupsByCouponOrReward = (
    paymentGroups: PaymentGroups,
    filterMethod: (paymentMethods: PaymentMethod<PaymentMethodMeta>[]) => PaymentMethod<PaymentMethodMeta>[],
): [PaymentGroups, PaymentGroups] => {
    let groups: { allGroups: PaymentGroups; filteredGroups: PaymentGroups } = {
        allGroups: [],
        filteredGroups: [],
    };

    groups = paymentGroups.reduce<typeof groups>((acc, cur) => {
        const methods = filterMethod(cur.paymentMethods);
        if (methods.length > 0) {
            acc.filteredGroups.push({ ...cur, paymentMethods: methods });
            acc.allGroups.push({
                ...cur,
                paymentMethods: cur.paymentMethods.filter((method) => methods.indexOf(method) === -1),
            });
        } else {
            acc.allGroups.push(cur);
        }
        return acc;
    }, groups);

    return [groups.allGroups, groups.filteredGroups];
};

/**
 *
 * @param paymentResponse
 * @returns [All PGs, coupon applicable groups, Filtered PGs]
 */
export const splitPaymentGroupsByCouponAndReward = (
    paymentResponse?: GetPaymentMethodsResponse | null,
): [PaymentGroups, PaymentGroups, PaymentGroups] => {
    if (!paymentResponse) {
        return [[], [], []];
    }

    let res: [PaymentGroups, PaymentGroups] = [paymentResponse.paymentGroup, paymentResponse.paymentGroup];

    if (paymentResponse.couponApplied) {
        // split by coupon
        res = _splitPaymentGroupsByCouponOrReward(paymentResponse.paymentGroup, getCouponApplicableMethods);
    }

    const couponGroups = res[1];

    if (paymentResponse.rewardApplied) {
        // split by reward
        res = _splitPaymentGroupsByCouponOrReward(couponGroups, getRewardApplicableMethods);
    }

    return [res[0], couponGroups, res[1]];
};

export const isFrictionPaymentGroup = (
    paymentGroup: PaymentGroupData<PaymentGroupMeta, PaymentMethod<PaymentMethodMeta>>,
): boolean => paymentGroup?.isFrictionHiddenGroup ?? false;

const _splitRenderingPaymentGroups = (paymentGroups: PaymentGroups): [PaymentGroups, PaymentGroups, PaymentGroups] => {
    let groups: { topGroups: PaymentGroups; frictionGroups: PaymentGroups; bottomGroups: PaymentGroups } = {
        topGroups: [],
        frictionGroups: [],
        bottomGroups: [],
    };

    groups = paymentGroups.reduce<typeof groups>((acc, cur) => {
        const canRenderFrictionGroup = isFrictionPaymentGroup(cur) && cur.paymentMethods.length > 0;

        if (canRenderFrictionGroup) {
            acc.frictionGroups.push(cur);
        } else if (acc.frictionGroups.length > 0) {
            // push groups into bottom groups list when it is not friction group and
            // friction group list is not empty in order to show these below friction layout
            acc.bottomGroups.push(cur);
        } else if (acc.frictionGroups.length === 0) {
            acc.topGroups.push(cur);
        }
        return acc;
    }, groups);

    return [groups.topGroups, groups.frictionGroups, groups.bottomGroups];
};

export const splitRenderingPaymentGroups = (groups: PaymentGroups): [PaymentGroups, PaymentGroups, PaymentGroups] => {
    if (!Array.isArray(groups) || groups.length === 0) {
        return [[], [], []];
    }
    return _splitRenderingPaymentGroups(groups);
};

export const getFrictionLayoutId = (paymentResponse?: GetPaymentMethodsResponse | null): FRICTION_LAYOUT_ID =>
    paymentResponse?.frictionLayoutId ?? FRICTION_LAYOUT_ID.CONTROL;

export const isFrictionLayout = (frictionLayoutId: FRICTION_LAYOUT_ID): boolean =>
    frictionLayoutId === FRICTION_LAYOUT_ID.TWO_CLICK || frictionLayoutId === FRICTION_LAYOUT_ID.THREE_CLICK;

export const shouldSplitRenderingGroups = (
    isCouponApplicableSpecific: boolean,
    frictionLayoutId: FRICTION_LAYOUT_ID,
): boolean => !isCouponApplicableSpecific && isFrictionLayout(frictionLayoutId);

export const hasPaymentMethods = (paymentGroups: PaymentGroups): boolean => {
    return paymentGroups.some((group) => group.paymentMethods.length > 0);
};

export const getSelectedRewardName = (splitpayList: SplitPayData[]): string => {
    return splitpayList.find(isSplitPayMethodRewardAndSelected)?.displayName ?? "";
};

export const getPaymentCardToken = (method: PaymentMethod<CardPaymentMethodMeta>): string =>
    method.meta.cardToken ?? "";

export const getPaymentCardReference = (method: PaymentMethod<CardPaymentMethodMeta>): string =>
    method.meta.cardReference ?? "";

export const getValidCardReferences = (methods: CardPaymentMethodData[]): string[] =>
    methods.reduce<string[]>((acc, cur) => {
        const cardReference = getPaymentCardReference(cur);

        if (cardReference.length === 0) {
            return acc;
        }
        return [...acc, cardReference];
    }, []);

export const getPaymentAmount = (
    paymentData?: GetPaymentMethodsResponse | null,
    withCoupon?: boolean,
    withSplitPay?: boolean,
): Option<string> => {
    if (!paymentData) {
        return None;
    }

    if (withSplitPay) {
        return withCoupon
            ? Some(paymentData.paymentAmountWithSplitpay)
            : Some(paymentData.paymentAmountWithSplitpayWithoutDiscount);
    }

    // splitpay and payment coupon are mutually exclusive [SLACK - https://swiggy.slack.com/archives/C07ADG9M0B1/p1749096910310979]
    // Glossary
    //  CV - Cart value
    //  RC - Restaurant coupon
    //  PC - Payment coupon
    // if a payment coupon is applied -
    //  1. paymentAmountWithPayDiscount will always be present
    //  2. billTotal (To be shown on filtered payment methods page) = CV - RC - PC
    //  3. billTotal (To be shown on other payment methods page) = CV - RC
    // Values in fields will be
    //  paymentAmount = CV - RC
    //  paymentAmountWithoutDiscount = CV
    //  paymentAmountWithPayDiscount = CV - RC - PC
    const withPaymentCoupon =
        Object.hasOwnProperty.call(paymentData, "paymentCouponCode") && !isEmpty(paymentData.paymentCouponCode);

    if (withPaymentCoupon) {
        return withCoupon ? Some(paymentData.paymentAmountWithPayDiscount) : Some(paymentData.paymentAmount);
    }

    return withCoupon ? Some(paymentData.paymentAmount) : Some(paymentData.paymentAmountWithoutDiscount);
};

export const getOrderConfirmationRedirectUrl = (url?: string): string => {
    return url || `${location.protocol}//${location.host}/checkout/order-received`;
};

export const getAddMoneyRedirectUrl = (isPaymentModeFlow: boolean, walletCode: string, url?: string): string => {
    if (url) {
        return url;
    }
    if (isPaymentModeFlow) {
        return `${location.origin}/payment-modes/wallet-detail?walletCode=${walletCode}`;
    }

    return `${location.origin}/wallet/money-received`;
};

export const getPaymentBankCode = (method: PaymentMethod<NetBankingPaymentMethodMeta>): string =>
    method?.meta?.code ?? "";

export const isFoodCard = (method: PaymentMethod<CardPaymentMethodMeta>): method is CardPaymentMethodData =>
    method.meta.isFoodCard ?? false;

export function isFoodcardGroup(group: PaymentGroupDataDef): group is ICardPaymentGroup["paymentData"] {
    return group.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.FOOD_CARD;
}

export function isFoodCardBalanceSufficient(balance: number, billAmount: number): boolean {
    return billAmount <= balance;
}

export const getPaymentHeaderSubtitleForSubRoutes = ({
    selectedAddressAnnotation,
    cartAmount,
    itemCount,
}: {
    selectedAddressAnnotation?: string | null;
    cartAmount: string;
    itemCount?: number;
}): string => {
    if (!itemCount || !selectedAddressAnnotation || isNaN(itemCount)) {
        return format(TextDict.TO_PAY_X, cartAmount);
    }

    return format(
        itemCount > 1 ? TextDict.X_ITEMS_DELIVERYING_Y_TO_PAY_Z : TextDict.X_ITEM_DELIVERYING_Y_TO_PAY_Z,
        String(itemCount),
        selectedAddressAnnotation,
        cartAmount,
    );
};

export const getPaymentHeaderSubtitleForSubRoutesV4 = ({
    selectedAddressAnnotation,
    cartAmount,
    itemCount,
    presentationDetails,
}: {
    selectedAddressAnnotation?: string | null;
    cartAmount: string;
    itemCount?: number;
    presentationDetails?: PresentationDetails;
}): string => {
    // for MRO and AIP the payment header is driven by BE instead of App
    if (isBusinessLineDash() || (isMROFlow() && isBusinessLineRegular())) {
        return presentationDetails?.headerSubtitle
            ? `${presentationDetails.headerSubtitle} \u20B9${cartAmount}`
            : format(TextDict.TO_PAY_X, cartAmount);
    }

    if (!itemCount || !selectedAddressAnnotation || isNaN(itemCount)) {
        return format(TextDict.TO_PAY_X, cartAmount);
    }

    return format(itemCount > 1 ? TextDict.X_ITEMS_TOTAL_Y : TextDict.X_ITEM_TOTAL_Y, String(itemCount), cartAmount);
};

export function findNewVpaPaymentMethod(
    paymentResponse: GetPaymentMethodsResponse,
    paymentMethodCode: string,
): Option<UpiPaymentMethod> {
    const upiMethod = Some(paymentResponse.paymentGroup.find(isUPIGroup)).andThen((group) =>
        getPaymentMethodFromGroup(group, paymentMethodCode),
    );

    return upiMethod.orElse(() =>
        Some(paymentResponse.paymentGroup.find(isPreferredGroup)).andThen((group) =>
            getPaymentMethodFromGroup(group, paymentMethodCode),
        ),
    );
}

export function getAllMethodsOfGroup<T extends PaymentMethod<PaymentMethodMeta>>(
    paymentGroups: GetPaymentMethodsResponse["paymentGroup"],
    groupName: string,
): T[] {
    return paymentGroups.reduce<T[]>((acc, group) => {
        const methods = group.paymentMethods.filter(
            (method) => method.groupName.toLowerCase() === groupName.toLowerCase(),
        );

        acc.push(...(methods as T[]));
        return acc;
    }, []);
}

export function getAllFoodCards(paymentResponse: GetPaymentMethodsResponse): CardPaymentMethodData[] {
    return [
        ...getAllMethodsOfGroup<CardPaymentMethodData>(
            paymentResponse.paymentGroup,
            VALID_PAYMENT_GROUP_TYPES.FOOD_CARD,
        ),
        ...getAllMethodsOfGroup<CardPaymentMethodData>(paymentResponse.paymentGroup, VALID_PAYMENT_GROUP_TYPES.CARD),
    ].filter(isFoodCard);
}

export const isUpiPaymentMethod = (method: PaymentMethod<PaymentMethodMeta>): method is UpiPaymentMethod => {
    return method.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.UPI;
};

export const isCardPaymentMethod = (method: PaymentMethod<PaymentMethodMeta>): method is CardPaymentMethodData => {
    return [VALID_PAYMENT_GROUP_TYPES.CARD, VALID_PAYMENT_GROUP_TYPES.FOOD_CARD].includes(
        method.groupName.toUpperCase(),
    );
};

export const isNewCardPaymentMethod = (method: PaymentMethod<PaymentMethodMeta>): boolean => {
    return method.name.toUpperCase() === VALID_PAYMENT_METHODS.NEW_CARD;
};

export const isNetBankingPaymentMethod = (
    method: PaymentMethod<PaymentMethodMeta>,
): method is NetBankingPaymentMethodData => {
    return method.groupName.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.NETBANKING;
};

export const isNewUpiPaymentMethod = (method: PaymentMethod<PaymentMethodMeta>): boolean => {
    return method.name === VALID_PAYMENT_METHODS.NEW_UPI;
};

export const isPaymentInstrumentAvailable = (paymentGroups: PaymentGroups, groupType: string): boolean => {
    const isAvailable = paymentGroups.some((group) => group.groupName.toUpperCase() === groupType);

    if (isAvailable) {
        return true;
    }
    // checking if instrument exists in preferred group
    const preferredGroup = paymentGroups.find(isPreferredGroup);

    if (preferredGroup == null) {
        return false;
    }
    return preferredGroup.paymentMethods.some((method) => method.groupName.toUpperCase() === groupType);
};

export const getPlaceOrderPaymentMethodName = (payload: ProcessOrderActionPayload): string => {
    try {
        return payload.placeOrderData.paymentMethodData.paymentCode ?? "";
    } catch (err) {
        // handling the case if place order data is null;
        // returning empty val in order to confirm the order so that user doesn't see duplicate error
        captureError(err);
        return "";
    }
};

export function getConfirmOrderTexts(payload: ConfirmOrderTextsPayload): { [state: string]: string } {
    const {
        isPostOrderTip = false,
        businessLine,
        tickState,
        isPriorityDelivery = false,
        isPennyDrop = false,
        isToing = false,
        successDataInfo,
        isPrebookPrepaidCart,
    } = payload;
    if (isPostOrderTip) return POST_ORDER_TIPPING_TICK_STATE[tickState];
    if (isPriorityDelivery) return POST_ORDER_PRIORITY_DELIVERY_STATE[tickState];
    if (isPennyDrop) return PENNY_DROP_TICK_STATE[tickState];

    switch (businessLine) {
        case MARKETPLACE_BUSINESS_LINE.SWIGGY_ONE:
        case MARKETPLACE_BUSINESS_LINE.SWIGGY_SUPER:
            return SUPER_TICK_STATE[tickState];

        case MARKETPLACE_BUSINESS_LINE.POST_ORDER_TIP:
            return POST_ORDER_TIPPING_TICK_STATE[tickState];

        case MARKETPLACE_BUSINESS_LINE.SWIGGY_MONEY:
            if (
                isOriginalBusinessLineSwiggyMoney() &&
                isSessionStorageAvailable &&
                sessionStorage.getItem(GIFT_DETAILS_STORAGE_KEY)
            ) {
                return SWIGGY_MONEY_GENERIC_TICK_STATE_GIFT[tickState];
            }
            if (isOriginalBusinessLineSwiggyMoney()) {
                return SWIGGY_MONEY_GENERIC_TICK_STATE[tickState];
            }
            return SWIGGY_MONEY_TICK_STATE[tickState];

        case MARKETPLACE_BUSINESS_LINE.DINEOUT:
            // Use successDataInfo if available for steal deal success states, otherwise fallback to default
            // use stealDeal
            if (isPrebookPrepaidCart && !isEmpty(successDataInfo) && tickState !== TICK_STATES.LOADING) {
                return {
                    ...DINEOUT_STEAL_DEAL_TICK_STATE[tickState],
                    main: successDataInfo?.main || DINEOUT_STEAL_DEAL_TICK_STATE[tickState].main,
                    sub: successDataInfo?.sub || DINEOUT_STEAL_DEAL_TICK_STATE[tickState].sub,
                };
            }
            return DINEOUT_TICK_STATE[tickState];

        case MARKETPLACE_BUSINESS_LINE.PAY_ON_DELIVERY:
            return isToing ? PAY_ON_DELIVERY_TOING_TICK_STATE[tickState] : PAY_ON_DELIVERY_TICK_STATE[tickState];

        case MARKETPLACE_BUSINESS_LINE.DASH:
            if (isCategoryInstamart()) {
                return INSTAMART_TICK_STATE[tickState];
            }
            return DEFAULT_TICK_STATE[tickState];

        case MARKETPLACE_BUSINESS_LINE.ASSURE:
            if (isSessionStorageAvailable && sessionStorage.getItem(ASSURE_PAY_ALL_DUES_STORAGE_KEY)) {
                return ASSURE_PAY_ALL_DUES_TICK_STATE[tickState];
            }
            return DEFAULT_TICK_STATE[tickState];

        case MARKETPLACE_BUSINESS_LINE.JUST:
            return JUST_TICK_STATE[tickState];

        default:
            return isToing ? DEFAULT_TOING_TICK_STATE[tickState] : DEFAULT_TICK_STATE[tickState];
    }
}

export const getReferrerPaymentsPageRoute = (): CheckoutRoute => {
    const referrerRoute = isBusinessLineSwiggyMoney() ? CheckoutRoute.SWIGGY_MONEY_PAYMENTS : CheckoutRoute.PAYMENTS;
    return referrerRoute;
};

export const getPaymentsCheckoutState = (): number => {
    return isBusinessLineSwiggyMoney()
        ? CheckoutActionTypes.SHOW_SWIGGYMONEY_PAYMENTS
        : CheckoutActionTypes.SHOW_PAYMENTS;
};

export const isMethodAlreadySelected = (currentPaymentId: string, newPaymentId: string): boolean =>
    currentPaymentId === newPaymentId;

export const getPaymentGroupHeadingWithFilter = (
    paymentGroups: GetPaymentMethodsResponse["paymentGroup"],
    rewardApplied?: boolean,
): string => {
    const rewardOrOfferString = rewardApplied ? "reward" : "offer";

    // when coupon/reward applicable to single payment group
    // and that payment group has only 1 payment method
    // which is "Add Card" change title to "No Saved cards"
    if (
        paymentGroups?.[0]?.groupName?.toUpperCase() === VALID_PAYMENT_GROUP_TYPES.CARD &&
        paymentGroups?.[0]?.paymentMethods.length === 1 &&
        paymentGroups?.[0]?.paymentMethods[0]?.name === VALID_PAYMENT_METHODS.NEW_CARD
    ) {
        return format(TextDict.NO_SAVED_CARDS, rewardOrOfferString);
    }

    return format(TextDict.PAYMENT_METHODS_APPLICABLE, rewardOrOfferString);
};

export const getPaymentFailedAlertSubInfo = (quantity: number): string => {
    if (!quantity) {
        return "";
    }
    const itemQuantityText = `${quantity} ${quantity === 1 ? TextDict.ITEM : TextDict.ITEMS}`;
    return format(TextDict.PAYMENT_ALERT_SUB_TEXT, itemQuantityText);
};

export const getPaymentMethodIconUrl = <T extends PaymentMethod<PaymentMethodMeta>>(paymentMethod: T): string => {
    const iconPath = paymentMethod.meta.iconUrl;
    return iconPath
        ? getCloudinaryUrl(iconPath, {
              grayscale: !isPaymentMethodEnabled(paymentMethod),
          })
        : "";
};

export const getCanShowDeliveryAddressView = (): boolean => {
    const businessLineSuper = isBusinessLineSuper();
    const businessLineSwiggyOne = isBusinessLineSwiggyOne();
    const businessLineDineout = isBusinessLineDineout();

    return !businessLineSuper && !businessLineDineout && !businessLineSwiggyOne;
};

//TODO: Remove this once the BE team(moments@work) sends only the cloudinary id and not the complete url
export const getCloudinaryIdFromURL = (url: string): string => url?.split("upload/")?.[1];

export const transformBalanceInfo = (balanceInfo: PaymentMethodBalanceInfo): GetWalletBalanceResponse => {
    const updatedLinkStatus = balanceInfo.isLinked
        ? VALID_WALLET_LINK_STATUSES.LINKED
        : VALID_WALLET_LINK_STATUSES.RETRY;
    return {
        linkStatus: updatedLinkStatus,
        balance: balanceInfo.balance,
        isOldWalletLinkFlow: balanceInfo.isOldWalletLinkFlow,
        mobileNumber: balanceInfo.mobileNumber,
    };
};

export const isMilestoneUnlockEligible = () =>
    (isSessionStorageAvailable && sessionStorage.getItem("habit_forming_milestone_unlock")) || false;

export const getEnabledPaymentMethods = <T extends PaymentMethod<PaymentMethodMeta>>(paymentMethods: T[]): T[] => {
    return paymentMethods.filter(isPaymentMethodEnabled);
};

export const isPaymentGroupEnabled = (paymentData: PaymentGroupDataDef): boolean => paymentData.enabled ?? true;

export const getSplitPayAmountsObj = (
    withSplitPay: boolean,
    selectedSplitpayMethods: string,
    splitPayList?: SplitPayData[],
): { [key: string]: string } => {
    if (!withSplitPay) {
        return {};
    }
    const splitpayList = splitPayList ?? [];
    const selectedSplitpayAmount: { [key: string]: string } = {};
    const selectedSplitpayMethodList = selectedSplitpayMethods.split(",");
    // selectedSplitpayMethods will have splitpay identifier which is different than payment code in case of TWID rewards. But, we need to send payment code to checkout.
    for (const methodName of selectedSplitpayMethodList) {
        const splitpayData = splitpayList.find(
            (item) => item.paymentCode === methodName || item.splitPayIdentifier === methodName,
        );
        selectedSplitpayAmount[splitpayData?.paymentCode || methodName] = splitpayData?.eligibleBalance || "0";
    }
    return selectedSplitpayAmount;
};

export const getTwidRewardsMetaObject = (
    withSplitPay: boolean,
    selectedSplitpayMethods: string,
    splitPayList?: SplitPayData[],
): { [key: string]: string } => {
    if (!withSplitPay) {
        return {};
    }

    const splitpayList = splitPayList ?? [];
    const twidRewardsObject: { [key: string]: string } = {};
    const selectedSplitpayMethodList = selectedSplitpayMethods.split(",");

    for (const methodName of selectedSplitpayMethodList) {
        const splitpayData = splitpayList.find(
            (item) => item.paymentCode === methodName || item.splitPayIdentifier === methodName,
        );

        if (!isEmpty(splitpayData) && splitpayData?.paymentCode === PAYMENT_CODES.TWID_REWARD) {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            Object.assign(twidRewardsObject, { reward_id: splitpayData?.meta?.rewardId || "" });
            break;
        }
    }

    return twidRewardsObject;
};

export const getTotalSplitPayAmountUsed = (selectedSplitPayAmountObj: { [key: string]: string }) =>
    Object.values(selectedSplitPayAmountObj).reduce((prevVal, currVal) => prevVal + Number(currVal), 0);

export const isTransitionScreenEnabled = (orderData?: OrderData): boolean => {
    const platform = (GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "").toLowerCase();
    const isApp = isiOSPlatform(platform) || isAndroidPlatform(platform);

    return orderData?.orderTransitionScreenNewMsgEnabled === true && isApp;
};

export const getChildOrdersCount = (orderData?: OrderData): number => orderData?.childOrders?.length ?? 0;

export const isMroOrderConfirmationExperienceEnabled = (orderData?: OrderData): boolean =>
    (orderData?.mroOrderData?.isMroExperienceEnabledOnOrderConfirmationScreen ?? false) &&
    getChildOrdersCount(orderData) > 1;

export const getPaymentCodeFromOrderData = (orderData?: PlaceOrderData) => {
    return orderData?.paymentMethodData?.paymentCode;
};

const getMetaData = (orderData?: OrderData): IMetaData | undefined => {
    const orderInfo = orderData?.rawResponse?.order_data;
    const metaDataJson = safeJsonParse<IMetaData>(orderInfo?.orders?.[0]?.order_jobs?.[0]?.metadata || "");

    return metaDataJson;
};

export const getSuccessInfoData = (orderData?: OrderData) => {
    const metaDataJson = getMetaData(orderData);

    const tickScreenSuccess = {
        main: metaDataJson?.successInfo?.message,
        sub: metaDataJson?.successInfo?.savingText,
    };
    return tickScreenSuccess;
};

export const isPrebookPrepaid = (orderData?: OrderData): boolean => {
    const metaDataJson = getMetaData(orderData);
    return metaDataJson?.dealCategory === PreebookPrepaid;
};
